import React, { useEffect } from 'react';
import styled from 'styled-components';

/* Inline SVG turbulence — no request, no canvas, no JS to generate it.
   Applied as a React inline style rather than inside the styled-component
   below: styled-components pipes its template through stylis, and this data
   URI desyncs that parser badly enough that every rule declared after it gets
   silently re-nested inside .grain, which is a very quiet way to lose two
   layers. An inline style never reaches the preprocessor. */
const NOISE =
  'url("data:image/svg+xml,%3Csvg xmlns=%27http://www.w3.org/2000/svg%27 width=%27180%27 height=%27180%27%3E%3Cfilter id=%27n%27%3E%3CfeTurbulence type=%27fractalNoise%27 baseFrequency=%270.82%27 numOctaves=%274%27 stitchTiles=%27stitch%27/%3E%3C/filter%3E%3Crect width=%27100%25%27 height=%27100%25%27 filter=%27url(%23n)%27/%3E%3C/svg%3E")';

/* The page's ground. Three fixed layers behind everything, none of which
   animate on their own — the only movement is the cursor's, which the visitor
   is already making.

   Every strength is a token in variables.js (--fx-*), so the whole treatment
   can be tuned or removed from one place.

   A fourth layer, a WebGL light pillar, used to sit between depth and grain.
   It was switched off and left here commented out, which is how it came to be
   an unused import, an unused hook call, a dead CSS rule and a dead token all
   at once. The component itself is still at src/components/lightpillar if it
   is ever wanted; nothing else references it.

   z-index: -1 puts these behind the content in normal flow while still
   painting over the body's background colour. Nothing here takes the pointer. */
const StyledBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: -1;
  pointer-events: none;

  /* The page colour, painted again here on purpose.
     position: fixed makes this element a stacking context, which isolates the
     blend group: a child with mix-blend-mode composites against what is inside
     this element and never against the body's background behind it. With
     nothing here to blend against, the grain's multiply resolved against
     transparency and came out identical to normal compositing, which is why
     turning it up in the light theme did almost nothing. The body already
     paints this exact colour, so nothing changes visually except that the
     blend now has a backdrop. */
  background-color: var(--bg);

  .fx {
    position: absolute;
    inset: 0;
  }

  /* Light from above, vignette below. Entirely static — it gives the page a
     top and a bottom instead of an even void. */
  .depth {
    background: radial-gradient(
        130% 70% at 50% -12%,
        rgba(var(--fx-depth-light-rgb), var(--fx-depth-light)),
        transparent 62%
      ),
      radial-gradient(
        110% 80% at 50% 112%,
        rgba(var(--fx-depth-vignette-rgb), var(--fx-depth-vignette)),
        transparent 58%
      );
  }

  /* Static noise. The most important of the four: near-black gradients band
     badly on 8-bit panels, and grain hides that while reading as a material
     rather than an effect.

     Only the opacity lives here. The image itself is applied inline on the
     element — see NOISE below for why it cannot go in this template. */
  .grain {
    opacity: var(--fx-grain);
    /* normal over a dark page, where the speckle adds light; multiply over a
       light one, where it has to take light away or it does nothing at all. */
    mix-blend-mode: var(--fx-grain-blend);
  }

  /* A dot lattice that exists everywhere but is masked to a radius around the
     pointer, so the page is bare until you move. Quieter than the rest by
     design. */
  .lattice {
    background-image: radial-gradient(
      circle at 1px 1px,
      rgba(var(--fx-lattice-rgb), var(--fx-lattice)) 1px,
      transparent 0
    );
    background-size: 24px 24px;
    -webkit-mask-image: radial-gradient(
      var(--fx-lattice-radius) circle at var(--fx-x) var(--fx-y),
      #000 0%,
      transparent 72%
    );
    mask-image: radial-gradient(
      var(--fx-lattice-radius) circle at var(--fx-x) var(--fx-y),
      #000 0%,
      transparent 72%
    );
  }

  /* A neutral lift under the pointer — no hue, so it never competes with the
     accent. */
  .halo {
    background: radial-gradient(
      var(--fx-halo-radius) circle at var(--fx-x) var(--fx-y),
      rgba(var(--fx-halo-rgb), var(--fx-halo)),
      transparent 62%
    );
  }

  /* Without a fine pointer these two follow nothing, so they are dropped
     rather than left sitting at their default position. */
  @media (hover: none), (pointer: coarse) {
    .halo,
    .lattice {
      display: none;
    }
  }
`;

const Backdrop = () => {
  useEffect(() => {
    // Touch devices never show the pointer-driven layers, so don't spend a
    // listener or a frame on them there.
    const fine = window.matchMedia('(hover: hover) and (pointer: fine)');
    if (!fine.matches) {
      return undefined;
    }

    const root = document.documentElement;
    let x = 0;
    let y = 0;
    let queued = false;

    // Coalesced to one write per frame, and nothing is read back, so this
    // never forces a layout — the layers repaint straight from the variables.
    const flush = () => {
      queued = false;
      root.style.setProperty('--fx-x', `${x}px`);
      root.style.setProperty('--fx-y', `${y}px`);
    };

    const onMove = e => {
      x = e.clientX;
      y = e.clientY;
      if (!queued) {
        queued = true;
        window.requestAnimationFrame(flush);
      }
    };

    window.addEventListener('pointermove', onMove, { passive: true });
    return () => window.removeEventListener('pointermove', onMove);
  }, []);

  return (
    <StyledBackdrop aria-hidden="true">
      <div className="fx depth" />

      <div className="fx grain" style={{ backgroundImage: NOISE }} />
      <div className="fx lattice" />
      <div className="fx halo" />
    </StyledBackdrop>
  );
};

export default Backdrop;
